import React from "react";

import {
  useAssignEmployeePortfoliosProperties,
  useGetEmployeePortfoliosProperties,
} from "manager/hooks/api";
import { getIntegrationType } from "manager/utils/integration.util";

import { INTEGRATION_TYPES, NOTIFICATIONS } from "shared/config/constants";
import { useDeviceType, useActiveCompany } from "shared/hooks";
import useModal from "shared/hooks/useModal";
import { openNotification } from "shared/utils/ui";

import AssignEmployeeToPropertiesModal from "./AssignEmployeeToPropertiesModal";
import AssignEmployeeToPropertiesSuccessModal from "./AssignEmployeeToPropertiesSuccessModalContainer";
import { AssignEmployeePortfoliosProperties } from "./interfaces";

export interface InviteAssignmentCompleteResult {
  skipped: boolean;
  assignedPropertyCount: number;
  newlyAssignedProperties?: { id: number; yardi_connected?: boolean }[];
}

interface AssignEmployeeToPropertiesModalContainerProps {
  employeeId: number;
  firstName: string;
  lastName: string;
  /** When true, re-labels actions for the invite-employee wizard (TI-2067). */
  inviteFlow?: boolean;
  onInviteAssignmentComplete?: (result: InviteAssignmentCompleteResult) => void;
}

const AssignEmployeeToPropertiesModalContainer = ({
  employeeId,
  firstName,
  lastName,
  inviteFlow = false,
  onInviteAssignmentComplete,
}: AssignEmployeeToPropertiesModalContainerProps) => {
  const { activeCompany } = useActiveCompany();
  const { isMobile } = useDeviceType();
  const { closeActiveModalDialog, openModalDialog } = useModal();
  const { portfolios, isPortfoliosLoading } =
    useGetEmployeePortfoliosProperties(employeeId);

  const {
    assignEmployeePortfoliosProperties,
    isAssignEmployeePortfoliosPropertiesLoading,
  } = useAssignEmployeePortfoliosProperties(employeeId);

  const anyNewPropertyHasIntegration = (newlyAssignedProperties) => {
    const foundIntegrationType = (newlyAssignedProperties || [])
      .map((property) => getIntegrationType(property))
      .find((integrationType) => !!integrationType);
    let existsPropertyWithIntegration = !!foundIntegrationType;
    if (foundIntegrationType === INTEGRATION_TYPES.YARDI) {
      existsPropertyWithIntegration = !activeCompany?.yardiSuspended;
    }
    return existsPropertyWithIntegration;
  };

  const handleInviteSkip = () => {
    if (inviteFlow && onInviteAssignmentComplete) {
      onInviteAssignmentComplete({
        skipped: true,
        assignedPropertyCount: 0,
      });
      // Success modal is opened by the callback via openModalDialog (replacing this modal).
      // Do not call closeActiveModalDialog — ModalManager.closeAll() would hide every modal,
      // including the one just opened.
      return;
    }
    closeActiveModalDialog();
  };

  const onSubmit = (values: AssignEmployeePortfoliosProperties) => {
    assignEmployeePortfoliosProperties(values, {
      onSuccess: ({ data }) => {
        if (inviteFlow && onInviteAssignmentComplete) {
          const newlyAssigned = data.newlyAssignedProperties || [];
          onInviteAssignmentComplete({
            skipped: false,
            assignedPropertyCount: newlyAssigned.length,
            newlyAssignedProperties: newlyAssigned,
          });
          return;
        }
        if (anyNewPropertyHasIntegration(data.newlyAssignedProperties)) {
          const context = {
            employeeId,
            firstName,
            lastName,
            newlyAssignedProperties: data.newlyAssignedProperties,
          };
          openModalDialog(AssignEmployeeToPropertiesSuccessModal, context);
        } else {
          openNotification(
            "The employee has been successfully assigned to the selected properties",
            NOTIFICATIONS.info
          );
          closeActiveModalDialog();
        }
      },
    });
  };

  return (
    <AssignEmployeeToPropertiesModal
      firstName={firstName}
      lastName={lastName}
      portfolios={portfolios}
      onSubmit={onSubmit}
      loading={isPortfoliosLoading}
      isMobile={isMobile}
      submitting={isAssignEmployeePortfoliosPropertiesLoading}
      cancelLinkLabel={
        inviteFlow && onInviteAssignmentComplete ? "Skip" : undefined
      }
      onCancelLinkClick={
        inviteFlow && onInviteAssignmentComplete ? handleInviteSkip : undefined
      }
      dynamicAssignSubmitLabel={Boolean(
        inviteFlow && onInviteAssignmentComplete
      )}
    />
  );
};

export default AssignEmployeeToPropertiesModalContainer;
